// Phase 11 — unified lead timeline normalizer (pure, no JSX).
// Merges the lead audit log (activity_history) and qualification snapshots into
// one chronological, read-only event stream with day grouping + key-event tagging.

// action_type -> { category, label, key }
const ACTION_META = {
  created: { category: "system", label: "Lead created", key: true },
  ingestion: { category: "system", label: "Lead imported", key: true },
  assignment: { category: "assignment", label: "Lead assigned", key: true },
  owner_change: { category: "assignment", label: "Lead reassigned", key: true },
  stage_change: { category: "stage", label: "Stage changed", key: true },
  quality_override: { category: "qualification", label: "Quality overridden", key: true },
  qual_edit_requested: { category: "qualification", label: "Qualification edit requested", key: true },
  note_added: { category: "note", label: "Note added", key: false },
  note_edited: { category: "note", label: "Note edited", key: false },
  note_pinned: { category: "note", label: "Note pinned", key: true },
  note_unpinned: { category: "note", label: "Note unpinned", key: false },
  note_deleted: { category: "note", label: "Note deleted", key: false },
  activity_created: { category: "activity", label: "Activity created", key: true },
  activity_completed: { category: "activity", label: "Activity completed", key: true },
  activity_cancelled: { category: "activity", label: "Activity cancelled", key: false },
  activity_edited: { category: "activity", label: "Activity updated", key: false },
  activity_due_changed: { category: "activity", label: "Activity rescheduled", key: false },
  activity_priority_changed: { category: "activity", label: "Activity priority changed", key: false },
  activity_reassigned: { category: "activity", label: "Activity reassigned", key: false },
  followup_done: { category: "activity", label: "Follow-up completed", key: true },
  followup_snooze: { category: "activity", label: "Follow-up snoozed", key: false },
  payment: { category: "payment", label: "Payment recorded", key: true },
  deal_added: { category: "deal", label: "Deal details added", key: true },
  deal_edited: { category: "deal", label: "Deal details updated", key: true },
  proposal_sent: { category: "deal", label: "Proposal sent", key: true },
  deleted: { category: "system", label: "Lead deleted", key: true },
  restored: { category: "system", label: "Lead restored", key: true },
  review_resolved: { category: "system", label: "Review resolved", key: false },
  sla_reshuffle: { category: "system", label: "SLA reshuffle", key: true },
};

const fallbackMeta = (action) => ({
  category: "system",
  label: (action || "Event").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  key: false,
});

// Build a normalized, reverse-chronological event list.
// activityHistory: [{ action_type, old_value, new_value, note, user_name, created_at }]
// qualHistory:     [{ type, at, quality, score, status, flow_name, by_user_name, reasons }]
export function buildTimeline(activityHistory = [], qualHistory = []) {
  const events = [];

  (activityHistory || []).forEach((a, i) => {
    const meta = ACTION_META[a.action_type] || fallbackMeta(a.action_type);
    events.push({
      id: a.id || `ah-${i}`,
      category: meta.category,
      label: meta.label,
      key: meta.key,
      description: a.note || null,
      old_value: a.old_value || null,
      new_value: a.new_value || null,
      user_name: a.user_name || "System",
      at: a.created_at || null,
    });
  });

  // Only qualification *snapshots* (overrides already live in activity_history).
  (qualHistory || []).filter((q) => q.type === "qualification").forEach((q, i) => {
    const parts = [];
    if (q.quality) parts.push(`Quality ${q.quality}`);
    if (q.score !== undefined && q.score !== null) parts.push(`score ${q.score}`);
    if (q.status && q.status !== "Qualified") parts.push(q.status);
    events.push({
      id: `qh-${i}-${q.at || ""}`,
      category: "qualification",
      label: i === (qualHistory.filter((x) => x.type === "qualification").length - 1) ? "Qualification completed" : "Qualification updated",
      key: true,
      description: [parts.join(" · "), q.flow_name].filter(Boolean).join(" — ") || null,
      old_value: null,
      new_value: q.quality || null,
      user_name: q.by_user_name || "System",
      at: q.at || null,
    });
  });

  events.sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
  return events;
}

// Group events into { label, items } sections by calendar day (Today / Yesterday / date).
export function groupByDay(events = []) {
  const today = new Date().toDateString();
  const yest = new Date(Date.now() - 86400000).toDateString();
  const order = [];
  const map = {};
  events.forEach((e) => {
    const d = e.at ? new Date(e.at) : null;
    let label = "Earlier";
    if (d && !isNaN(d.getTime())) {
      const ds = d.toDateString();
      label = ds === today ? "Today" : ds === yest ? "Yesterday"
        : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    }
    if (!map[label]) { map[label] = []; order.push(label); }
    map[label].push(e);
  });
  return order.map((label) => ({ label, items: map[label] }));
}
